import { DashboardStats } from '../types/dashboard';

// API base URL - should be configured based on environment
const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || 'https://obta-api.trade.workers.dev'}/api`;

// API token for production
const API_TOKEN = 'obta-production-api-token-2025';

// Helper function to handle API errors
const handleApiError = (error: any): never => {
  console.error('Dashboard API Error:', error);
  throw new Error(error.message || 'An error occurred while fetching dashboard data');
};

export const DashboardService = {
  /**
   * Get total count of documents
   */
  async getDocumentsCount(): Promise<number> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents?limit=1&offset=0`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch documents count: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.total || 0;
    } catch (error) {
      console.warn('Failed to fetch documents count, returning 0:', error);
      return 0;
    }
  },

  /**
   * Get total count of collections
   */
  async getCollectionsCount(): Promise<number> {
    try {
      const response = await fetch(`${API_BASE_URL}/collections?limit=1&offset=0`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch collections count: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.total || 0;
    } catch (error) {
      console.warn('Failed to fetch collections count, returning 0:', error);
      return 0;
    }
  },

  /**
   * Get total count of users
   */
  async getUsersCount(): Promise<number> {
    try {
      const response = await fetch(`${API_BASE_URL}/users?limit=1&offset=0`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users count: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.total || 0;
    } catch (error) {
      console.warn('Failed to fetch users count, returning 0:', error);
      return 0;
    }
  },

  /**
   * Get all dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      // Fetch all counts in parallel
      const [documentsCount, collectionsCount, usersCount] = await Promise.all([
        this.getDocumentsCount(),
        this.getCollectionsCount(),
        this.getUsersCount(),
      ]);

      return {
        totalDocuments: documentsCount,
        totalCollections: collectionsCount,
        totalUsers: usersCount,
      };
    } catch (error) {
      return handleApiError(error);
    }
  },
};

export default DashboardService;
